import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Menu, 
  X, 
  Users, 
  Trophy, 
  Calendar, 
  Info, 
  UserPlus,
  Shield,
  ChevronDown,
  Lock,
  Image,
  Play
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8f61f4ec-e0f3-4bd9-bbd1-92126e3d51b2/artifacts/4yf0ok29_Logo_Lactal.png";
const LOGO_BLANCO = "https://customer-assets.emergentagent.com/job_club-tournament/artifacts/o6pxue64_logo_1_tinta.png";

// Components
import RegistrationModal from "./components/RegistrationModal";
import AdminPanel from "./components/AdminPanel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./components/ui/dialog";
import { Button } from "./components/ui/button";

// Position badge component with icons
const PositionBadge = ({ position }) => {
  const positionConfig = {
    "Golero": { icon: "🧤", label: "GOL" },
    "Defensa": { icon: "🛡️", label: "DEF" },
    "Volante": { icon: "⚙️", label: "VOL" },
    "Delantero": { icon: "⚽", label: "DEL" }
  };
  
  const config = positionConfig[position] || { icon: "👤", label: position };

  return (
    <span className="bg-white/10 border border-white/20 text-gray-300 px-2 py-1 text-xs font-medium rounded flex items-center gap-1">
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </span>
  );
};

// Player card component
const PlayerCard = ({ player, index }) => {
  return (
    <div 
      className="card-player flex items-center gap-4 animate-fade-in"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`player-card-${player.id}`}
    >
      <div className="w-12 h-12 rounded-full bg-lactal-gold/20 flex items-center justify-center text-lactal-gold font-heading font-bold text-xl">
        {player.apodo ? player.apodo[0].toUpperCase() : player.nombre[0].toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-heading text-lg font-bold text-white truncate">
          {player.apodo || `${player.nombre} ${player.apellido}`}
        </h3>
        <p className="text-sm text-gray-400">
          {player.nombre} {player.apellido} • {player.edad} años
        </p>
      </div>
      <div className="flex items-center gap-2">
        {player.comida_final && (
          <span className="bg-green-600/20 border border-green-500/30 text-green-400 px-2 py-1 text-xs font-medium rounded flex items-center gap-1" title="Se queda a la comida">
            🍕 Comida
          </span>
        )}
        <PositionBadge position={player.posicion} />
      </div>
    </div>
  );
};

// Sticky Header component
const Header = ({ onOpenModal, onNavigate, activeSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navItems = [
    { id: "info", label: "Info", icon: Info },
    { id: "inscriptos", label: "Inscriptos", icon: Users },
    { id: "equipos", label: "Equipos", icon: Shield },
    { id: "fixture", label: "Fixture", icon: Calendar },
    { id: "galeria", label: "Imágenes", icon: Image },
    { id: "quienes-somos", label: "Nosotros", icon: Users },
  ];

  const handleNav = (id) => {
    onNavigate(id);
    setIsMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass" data-testid="main-header">
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => handleNav("hero")}
            className="flex items-center gap-2"
            data-testid="logo-button"
          >
            <img src={LOGO_BLANCO} alt="LACTAL" className="h-8 w-auto" />
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => handleNav(item.id)}
                className={`text-sm font-medium transition-colors ${
                  activeSection === item.id 
                    ? "text-lactal-gold" 
                    : "text-gray-300 hover:text-white"
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={onOpenModal}
              className="btn-primary text-sm py-2"
              data-testid="header-inscribirse-btn"
            >
              <span>Inscribirse</span>
            </button>
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile nav */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-white/10 pt-4 animate-fade-in">
            <div className="flex flex-col gap-2">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => handleNav(item.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    activeSection === item.id 
                      ? "bg-lactal-gold/20 text-lactal-gold" 
                      : "text-gray-300 hover:bg-white/5"
                  }`}
                  data-testid={`mobile-nav-${item.id}`}
                >
                  <item.icon size={20} />
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => {
                  onOpenModal();
                  setIsMenuOpen(false);
                }}
                className="btn-primary mt-2 text-center"
                data-testid="mobile-inscribirse-btn"
              >
                <span>Inscribirse</span>
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

const MATA_BUZARDA_ICON = "https://customer-assets.emergentagent.com/job_club-tournament/artifacts/hl02wdqo_Matabuzarda.png";

// Info Modal component
const InfoModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-[#3a3a3a] border border-white/10 text-white max-w-md mx-auto"
        data-testid="info-comida-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase text-lactal-gold text-center">
            Información sobre la comida
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4 text-gray-300">
          <p>
            Cuando finalice el torneo, vamos a organizar una gran comida para todos los que quieran sumarse.
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-gray-400 text-lg mt-0.5">🍽</span>
              <div>
                <strong className="text-white">¿Qué incluye?</strong><br />
                <span className="text-gray-300">Incluye pizzas con gustos y chivitos.</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-gray-400 text-lg mt-0.5">🥤</span>
              <div>
                <strong className="text-white">¿Para tomar?</strong><br />
                <span className="text-gray-300">Además de la que te vas a tomar en el torneo, habrá refresco y agua. El alcohol lo lleva cada uno que quiera.</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-gray-400 text-lg mt-0.5">📅</span>
              <div>
                <strong className="text-white">¿Cuándo?</strong><br />
                <span className="text-gray-300">El último día del torneo.</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-gray-400 text-lg mt-0.5">📍</span>
              <div>
                <strong className="text-white">¿Dónde?</strong><br />
                <span className="text-gray-300">En las instalaciones del club (se confirmará lugar exacto).</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-gray-400 text-lg mt-0.5">💵</span>
              <div>
                <strong className="text-white">¿Cómo se paga?</strong><br />
                <span className="text-gray-300">Durante el torneo - <span className="text-lactal-gold font-bold uppercase">Solo en efectivo</span></span>
              </div>
            </div>
          </div>
          <p className="text-lactal-gold font-medium text-center pt-2 border-t border-white/10">
            Costo: $450 por persona
          </p>
        </div>
        <Button onClick={onClose} className="btn-primary w-full">
          <span>Entendido</span>
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Hero Section
const HeroSection = ({ onOpenModal, totalPlayers }) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0, finished: false });
  
  // Cuenta regresiva para el 3 de febrero 2026 a las 20:00 hs (Uruguay UTC-3)
  useEffect(() => {
    const targetDate = new Date('2026-02-03T23:00:00Z'); // 20:00 Uruguay = 23:00 UTC
    
    const updateCountdown = () => {
      const now = new Date();
      const diff = targetDate - now;
      
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0, finished: true });
        return;
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setCountdown({ days, hours, minutes, seconds, finished: false });
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <section 
      id="hero" 
      className="min-h-screen flex flex-col items-center justify-center px-4 pt-20 pb-12 relative"
      data-testid="hero-section"
    >
      <div className="text-center max-w-lg mx-auto">
        <img 
          src={LOGO_URL} 
          alt="Torneo LACTAL" 
          className="w-72 sm:w-80 lg:w-96 mx-auto mb-8 drop-shadow-2xl animate-fade-in"
          data-testid="hero-logo"
        />
        
        {countdown.finished ? (
          /* Cuando termina la cuenta regresiva */
          <div className="animate-fade-in">
            <p className="font-heading text-3xl sm:text-4xl lg:text-5xl text-lactal-gold uppercase tracking-wider animate-pulse">
              ¡Comenzó la lechona!
            </p>
            <img 
              src={MATA_BUZARDA_ICON} 
              alt="Mata Buzarda" 
              className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mt-6 animate-bounce"
            />
          </div>
        ) : (
          /* Cuenta regresiva activa */
          <div className="animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <p className="font-heading text-xl sm:text-2xl text-gray-300 uppercase tracking-widest mb-4">
              Faltan:
            </p>
            
            {/* Countdown */}
            <div className="flex justify-center gap-2 sm:gap-4 mb-6">
              <div className="bg-black/60 backdrop-blur-sm border border-lactal-gold/30 rounded-lg p-3 sm:p-4 min-w-[70px] sm:min-w-[90px]">
                <p className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black text-lactal-gold">
                  {countdown.days}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm uppercase tracking-wider">Días</p>
              </div>
              <div className="bg-black/60 backdrop-blur-sm border border-lactal-gold/30 rounded-lg p-3 sm:p-4 min-w-[70px] sm:min-w-[90px]">
                <p className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black text-lactal-gold">
                  {String(countdown.hours).padStart(2, '0')}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm uppercase tracking-wider">Horas</p>
              </div>
              <div className="bg-black/60 backdrop-blur-sm border border-lactal-gold/30 rounded-lg p-3 sm:p-4 min-w-[70px] sm:min-w-[90px]">
                <p className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black text-lactal-gold">
                  {String(countdown.minutes).padStart(2, '0')}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm uppercase tracking-wider">Min</p>
              </div>
              <div className="bg-black/60 backdrop-blur-sm border border-lactal-gold/30 rounded-lg p-3 sm:p-4 min-w-[70px] sm:min-w-[90px]">
                <p className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black text-white">
                  {String(countdown.seconds).padStart(2, '0')}
                </p>
                <p className="text-gray-400 text-xs sm:text-sm uppercase tracking-wider">Seg</p>
              </div>
            </div>
            
            <p className="font-heading text-lg sm:text-xl lg:text-2xl text-gray-300 uppercase tracking-wider">
              para que comience la <span className="text-lactal-gold font-bold">repartija de lechona</span>
            </p>
          </div>
        )}
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={32} className="text-gray-500" />
      </div>
      
      <InfoModal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} />
    </section>
  );
};

// Info Section
const InfoSection = ({ info }) => {
  const infoItems = [
    { label: "Lugar", value: info?.lugar || "Por definir", icon: "📍" },
    { label: "Fechas", value: info?.fechas || "Enero - Febrero 2026", icon: "📅" },
    { label: "Duración de partidos", value: info?.duracion_partidos || "Por definir", icon: "⏱️" },
    { label: "Formato", value: info?.formato || "Por definir", icon: "🏆" },
  ];

  return (
    <section id="info" className="py-16 px-4" data-testid="info-section">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title text-center mb-2">
          <Info className="inline-block mr-2 text-lactal-gold" size={32} />
          Información del Torneo
        </h2>
        <p className="section-subtitle text-center mb-8">
          Todo lo que necesitás saber
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {infoItems.map((item, idx) => (
            <div 
              key={item.label}
              className="bg-black/40 backdrop-blur-sm p-4 rounded-lg border border-white/10 animate-fade-in"
              style={{ animationDelay: `${idx * 0.1}s` }}
              data-testid={`info-item-${idx}`}
            >
              <span className="text-2xl mb-2 block">{item.icon}</span>
              <h3 className="font-heading text-sm uppercase tracking-wider text-gray-400 mb-1">
                {item.label}
              </h3>
              <p className="text-white font-medium">{item.value}</p>
            </div>
          ))}
        </div>

        {info?.reglamento && (
          <div className="bg-black/40 backdrop-blur-sm p-6 rounded-lg border border-white/10">
            <h3 className="font-heading text-lg uppercase tracking-wider text-lactal-gold mb-3">
              Reglamento
            </h3>
            <p className="text-gray-300 whitespace-pre-line">{info.reglamento}</p>
          </div>
        )}

        {info?.mensaje_extra && (
          <p className="text-center text-lactal-gold font-heading text-xl mt-8 uppercase">
            {info.mensaje_extra}
          </p>
        )}
      </div>
    </section>
  );
};

// Inscriptos Section
const InscriptosSection = ({ players, loading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const comidaCount = players.filter(p => p.comida_final).length;
  
  return (
    <section id="inscriptos" className="py-16 px-4" data-testid="inscriptos-section">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title text-center mb-2">
          <Users className="inline-block mr-2 text-lactal-gold" size={32} />
          Inscriptos
        </h2>
        <p className="section-subtitle text-center mb-4">
          {players.length > 0 
            ? `${players.length} jugadores anotados` 
            : "Sé el primero en anotarte"}
        </p>
        
        {players.length > 0 && (
          <div className="flex justify-center gap-6 mb-6">
            <div className="bg-black/30 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/10 text-center">
              <p className="text-lactal-gold font-heading text-2xl font-bold">{players.length}</p>
              <p className="text-gray-400 text-xs uppercase tracking-wider">Total</p>
            </div>
            <div className="bg-green-900/30 backdrop-blur-sm px-4 py-2 rounded-lg border border-green-500/30 text-center">
              <p className="text-green-400 font-heading text-2xl font-bold flex items-center gap-1 justify-center">
                🍕 {comidaCount}
              </p>
              <p className="text-green-400/70 text-xs uppercase tracking-wider">Para la comida</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-400 font-heading text-lg">Inflando las pelotas...</p>
          </div>
        ) : players.length === 0 ? (
          <div className="text-center py-12 bg-black/30 rounded-lg border border-white/5">
            <Users size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 font-heading text-lg">
              Aún no hay nadie inscripto.<br />
              El DT se quedó dormido.
            </p>
          </div>
        ) : (
          <div className="bg-black/30 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
            {/* Toggle Button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
              data-testid="inscriptos-toggle"
            >
              <span className="text-gray-300 font-medium">
                {isExpanded ? "Ocultar lista de inscriptos" : "Ver lista de inscriptos"}
              </span>
              <ChevronDown 
                className={`text-lactal-gold transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} 
                size={24} 
              />
            </button>
            
            {/* Collapsible List */}
            <div 
              className={`transition-all duration-300 ease-in-out overflow-hidden ${
                isExpanded ? 'max-h-[60vh] md:max-h-[500px]' : 'max-h-0'
              }`}
            >
              <div className="px-4 pb-4 overflow-y-auto max-h-[60vh] md:max-h-[480px] scrollbar-thin">
                <div className="grid gap-2 pt-2">
                  {players.map((player, idx) => (
                    <PlayerCard key={player.id} player={player} index={idx} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Equipos Section
const EquiposSection = ({ equipos, loading }) => {
  return (
    <section id="equipos" className="py-16 px-4" data-testid="equipos-section">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title text-center mb-2">
          <Shield className="inline-block mr-2 text-lactal-gold" size={32} />
          Equipos
        </h2>
        <p className="section-subtitle text-center mb-8">
          Conformación de equipos
        </p>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-400 font-heading text-lg">Cargando equipos...</p>
          </div>
        ) : equipos.length === 0 ? (
          <div className="text-center py-12 bg-black/30 backdrop-blur-sm rounded-lg border border-white/10">
            <Shield size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 font-heading text-lg mb-2">
              Equipos por armar
            </p>
            <p className="text-gray-500 text-sm">
              Una vez cerradas las inscripciones, la organización armará los equipos para que sean lo más parejos posibles.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {equipos.map((equipo, idx) => (
              <div 
                key={equipo.id}
                className="bg-black/40 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden animate-fade-in"
                style={{ animationDelay: `${idx * 0.1}s` }}
                data-testid={`equipo-card-${equipo.id}`}
              >
                <div 
                  className="p-4 border-b border-white/5"
                  style={{ borderLeftColor: equipo.color, borderLeftWidth: '4px' }}
                >
                  <h3 className="font-heading text-xl font-bold text-white uppercase">
                    {equipo.nombre}
                  </h3>
                  <p className="text-sm text-gray-400">
                    {equipo.jugadores?.length || 0} jugadores
                  </p>
                </div>
                <div className="p-4">
                  {equipo.jugadores && equipo.jugadores.length > 0 ? (
                    <div className="space-y-2">
                      {equipo.jugadores.map(jugador => (
                        <div key={jugador.id} className="flex items-center gap-3 text-sm">
                          <PositionBadge position={jugador.posicion} />
                          <span className="text-gray-300">
                            {jugador.apodo || `${jugador.nombre} ${jugador.apellido}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Sin jugadores asignados</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Tabla de Posiciones Component
const TablaPosiciones = ({ partidos, equipos }) => {
  // Calcular estadísticas de cada equipo
  const calcularEstadisticas = () => {
    const stats = {};
    
    // Inicializar stats para todos los equipos
    equipos.forEach(equipo => {
      stats[equipo.id] = {
        id: equipo.id,
        nombre: equipo.nombre,
        color: equipo.color,
        pj: 0,  // partidos jugados
        pg: 0,  // partidos ganados
        pe: 0,  // partidos empatados
        pp: 0,  // partidos perdidos
        gf: 0,  // goles a favor
        gc: 0,  // goles en contra
        dif: 0, // diferencia de gol
        pts: 0  // puntos
      };
    });
    
    // Solo contar partidos de fase de grupos (Fecha 1, 2, 3)
    const partidosGrupos = partidos.filter(p => 
      p.jugado && ['Fecha 1', 'Fecha 2', 'Fecha 3'].includes(p.fase)
    );
    
    partidosGrupos.forEach(partido => {
      const localId = partido.equipo_local?.id;
      const visitanteId = partido.equipo_visitante?.id;
      
      if (!localId || !visitanteId || !stats[localId] || !stats[visitanteId]) return;
      
      const golesLocal = partido.goles_local || 0;
      const golesVisitante = partido.goles_visitante || 0;
      
      // Actualizar stats del equipo local
      stats[localId].pj++;
      stats[localId].gf += golesLocal;
      stats[localId].gc += golesVisitante;
      
      // Actualizar stats del equipo visitante
      stats[visitanteId].pj++;
      stats[visitanteId].gf += golesVisitante;
      stats[visitanteId].gc += golesLocal;
      
      // Determinar resultado
      if (golesLocal > golesVisitante) {
        stats[localId].pg++;
        stats[localId].pts += 3;
        stats[visitanteId].pp++;
      } else if (golesLocal < golesVisitante) {
        stats[visitanteId].pg++;
        stats[visitanteId].pts += 3;
        stats[localId].pp++;
      } else {
        stats[localId].pe++;
        stats[localId].pts += 1;
        stats[visitanteId].pe++;
        stats[visitanteId].pts += 1;
      }
    });
    
    // Calcular diferencia de gol
    Object.values(stats).forEach(s => {
      s.dif = s.gf - s.gc;
    });
    
    // Ordenar: puntos > diferencia de gol > goles a favor
    return Object.values(stats).sort((a, b) => {
      if (b.pts !== a.pts) return b.pts - a.pts;
      if (b.dif !== a.dif) return b.dif - a.dif;
      return b.gf - a.gf;
    });
  };
  
  const tabla = calcularEstadisticas();
  
  // Si no hay equipos o no hay partidos jugados en fase de grupos, no mostrar
  const hayPartidosJugados = partidos.some(p => 
    p.jugado && ['Fecha 1', 'Fecha 2', 'Fecha 3'].includes(p.fase)
  );
  
  if (equipos.length === 0 || !hayPartidosJugados) return null;
  
  return (
    <div className="mb-8" data-testid="tabla-posiciones">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-lactal-gold/50 to-transparent"></div>
        <h3 className="font-heading text-lg text-lactal-gold uppercase tracking-wider px-2">
          Tabla de Posiciones
        </h3>
        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-lactal-gold/50 to-transparent"></div>
      </div>
      
      <div className="bg-black/40 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden">
        {/* Header - Desktop */}
        <div className="hidden sm:grid grid-cols-[auto_1fr_repeat(8,minmax(40px,1fr))] gap-2 p-3 bg-black/50 text-xs text-gray-400 uppercase font-medium">
          <span className="w-6 text-center">#</span>
          <span>Equipo</span>
          <span className="text-center">PJ</span>
          <span className="text-center">PG</span>
          <span className="text-center">PE</span>
          <span className="text-center">PP</span>
          <span className="text-center">GF</span>
          <span className="text-center">GC</span>
          <span className="text-center">DIF</span>
          <span className="text-center text-lactal-gold">PTS</span>
        </div>
        
        {/* Rows */}
        {tabla.map((equipo, idx) => (
          <div 
            key={equipo.id}
            className={`
              grid grid-cols-[auto_1fr_repeat(3,minmax(40px,1fr))] sm:grid-cols-[auto_1fr_repeat(8,minmax(40px,1fr))] 
              gap-2 p-3 items-center border-t border-white/5
            `}
          >
            {/* Posición */}
            <span className={`w-6 text-center font-bold ${
              idx === 0 ? 'text-lactal-gold' : 'text-gray-500'
            }`}>
              {idx + 1}
            </span>
            
            {/* Nombre del equipo */}
            <span 
              className="font-heading font-bold uppercase text-sm truncate"
              style={{ color: equipo.color }}
            >
              {equipo.nombre}
            </span>
            
            {/* Stats - Mobile: solo PJ, DIF, PTS */}
            <span className="text-center text-gray-400 sm:hidden">{equipo.pj}</span>
            <span className={`text-center sm:hidden ${equipo.dif > 0 ? 'text-green-400' : equipo.dif < 0 ? 'text-red-400' : 'text-gray-400'}`}>
              {equipo.dif > 0 ? '+' : ''}{equipo.dif}
            </span>
            <span className="text-center font-bold text-lactal-gold sm:hidden">{equipo.pts}</span>
            
            {/* Stats - Desktop: todos */}
            <span className="text-center text-gray-300 hidden sm:block">{equipo.pj}</span>
            <span className="text-center text-green-400 hidden sm:block">{equipo.pg}</span>
            <span className="text-center text-gray-400 hidden sm:block">{equipo.pe}</span>
            <span className="text-center text-red-400 hidden sm:block">{equipo.pp}</span>
            <span className="text-center text-gray-300 hidden sm:block">{equipo.gf}</span>
            <span className="text-center text-gray-300 hidden sm:block">{equipo.gc}</span>
            <span className={`text-center hidden sm:block ${equipo.dif > 0 ? 'text-green-400' : equipo.dif < 0 ? 'text-red-400' : 'text-gray-400'}`}>
              {equipo.dif > 0 ? '+' : ''}{equipo.dif}
            </span>
            <span className="text-center font-bold text-lactal-gold hidden sm:block">{equipo.pts}</span>
          </div>
        ))}
        
        {/* Leyenda */}
        <div className="p-3 bg-black/30 border-t border-white/10 text-xs text-gray-500">
          <p className="text-center">
            <span className="text-lactal-gold">Semifinales:</span> 1° vs 4° y 2° vs 3°
          </p>
        </div>
      </div>
    </div>
  );
};

// Fixture Section
const FixtureSection = ({ partidos, equipos, loading }) => {
  // Agrupar partidos por fase
  const partidosPorFase = partidos.reduce((acc, partido) => {
    const fase = partido.fase || 'Fase de Grupos';
    if (!acc[fase]) acc[fase] = [];
    acc[fase].push(partido);
    return acc;
  }, {});
  
  // Orden de las fases
  const ordenFases = ['Fecha 1', 'Fecha 2', 'Fecha 3', 'Semifinales', 'Tercer Puesto', 'Final'];
  const fasesOrdenadas = ordenFases.filter(f => partidosPorFase[f]);
  
  // Si no hay fases definidas, mostrar todos juntos
  const faseKeys = fasesOrdenadas.length > 0 ? fasesOrdenadas : Object.keys(partidosPorFase);
  
  return (
    <section id="fixture" className="py-16 px-4" data-testid="fixture-section">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title text-center mb-2">
          <Calendar className="inline-block mr-2 text-lactal-gold" size={32} />
          Fixture y Resultados
        </h2>
        <p className="section-subtitle text-center mb-8">
          Calendario de partidos
        </p>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-400 font-heading text-lg">Cargando fixture...</p>
          </div>
        ) : partidos.length === 0 ? (
          <div className="text-center py-12 bg-black/30 backdrop-blur-sm rounded-lg border border-white/10">
            <Calendar size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 font-heading text-lg mb-2">
              Fixture por definir
            </p>
            <p className="text-gray-500 text-sm">
              El fixture se armará una vez conformados los equipos.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Tabla de Posiciones */}
            <TablaPosiciones partidos={partidos} equipos={equipos} />
            
            {faseKeys.map((fase) => (
              <div key={fase}>
                {/* Fase Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-lactal-gold/50 to-transparent"></div>
                  <h3 className="font-heading text-lg text-lactal-gold uppercase tracking-wider px-2">
                    {fase}
                  </h3>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-lactal-gold/50 to-transparent"></div>
                </div>
                
                {/* Partidos de esta fase */}
                <div className="space-y-3">
                  {partidosPorFase[fase].map((partido, idx) => (
                    <div 
                      key={partido.id}
                      className={`bg-black/40 backdrop-blur-sm rounded-lg border overflow-hidden animate-fade-in ${
                        partido.jugado ? 'border-lactal-gold/30' : 'border-white/10'
                      }`}
                      style={{ animationDelay: `${idx * 0.1}s` }}
                      data-testid={`partido-card-${partido.id}`}
                    >
                      <div className="p-3 sm:p-4">
                        {/* Info del partido (fecha, hora, cancha) */}
                        {(partido.fecha || partido.cancha) && (
                          <p className="text-xs text-gray-500 mb-3 text-center">
                            {partido.fecha && partido.fecha} 
                            {partido.hora && ` • ${partido.hora}`} 
                            {partido.cancha && ` • ${partido.cancha}`}
                          </p>
                        )}
                        
                        {/* Layout Mobile: Vertical */}
                        <div className="flex flex-col sm:hidden gap-2">
                          {/* Equipo Local */}
                          <div 
                            className="flex items-center justify-between p-2 rounded bg-black/30"
                            style={{ borderLeft: `3px solid ${partido.equipo_local?.color || '#666'}` }}
                          >
                            <span 
                              className="font-heading font-bold text-sm uppercase"
                              style={{ color: partido.equipo_local?.color || '#fff' }}
                            >
                              {partido.equipo_local?.nombre || "Por definir"}
                            </span>
                            {partido.jugado && (
                              <span className="font-heading text-xl font-black text-white bg-black/50 px-3 py-1 rounded">
                                {partido.goles_local}
                              </span>
                            )}
                          </div>
                          
                          {/* VS o marcador central en mobile */}
                          {!partido.jugado && (
                            <div className="text-center py-1">
                              <span className="text-gray-500 font-heading text-sm">VS</span>
                            </div>
                          )}
                          
                          {/* Equipo Visitante */}
                          <div 
                            className="flex items-center justify-between p-2 rounded bg-black/30"
                            style={{ borderLeft: `3px solid ${partido.equipo_visitante?.color || '#666'}` }}
                          >
                            <span 
                              className="font-heading font-bold text-sm uppercase"
                              style={{ color: partido.equipo_visitante?.color || '#fff' }}
                            >
                              {partido.equipo_visitante?.nombre || "Por definir"}
                            </span>
                            {partido.jugado && (
                              <span className="font-heading text-xl font-black text-white bg-black/50 px-3 py-1 rounded">
                                {partido.goles_visitante}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Layout Desktop: Horizontal */}
                        <div className="hidden sm:flex items-center justify-between gap-4">
                          <div className="flex-1 text-right">
                            <p 
                              className="font-heading text-lg font-bold text-white uppercase truncate"
                              style={{ color: partido.equipo_local?.color }}
                            >
                              {partido.equipo_local?.nombre || "Por definir"}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2 px-4 py-2 bg-black/30 rounded-lg min-w-[100px] justify-center">
                            {partido.jugado ? (
                              <>
                                <span className="font-heading text-2xl font-black text-white">
                                  {partido.goles_local}
                                </span>
                                <span className="text-gray-500">-</span>
                                <span className="font-heading text-2xl font-black text-white">
                                  {partido.goles_visitante}
                                </span>
                              </>
                            ) : (
                              <span className="text-gray-500 font-heading">VS</span>
                            )}
                          </div>
                          
                          <div className="flex-1 text-left">
                            <p 
                              className="font-heading text-lg font-bold text-white uppercase truncate"
                              style={{ color: partido.equipo_visitante?.color }}
                            >
                              {partido.equipo_visitante?.nombre || "Por definir"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Opinion Section
const TU_OPINION_ICON = "https://customer-assets.emergentagent.com/job_club-tournament/artifacts/fkn1ngpp_tuOpinion.png";

const OpinionModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="bg-[#3a3a3a] border border-white/10 text-white max-w-md mx-auto"
        data-testid="opinion-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase text-lactal-gold text-center">
            Tu opinión nos importa
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 text-center">
          <p className="text-gray-300 mb-4">
            Este torneo se destaca por su <span className="text-white font-semibold">transparencia</span>, <span className="text-white font-semibold">compromiso</span> y <span className="text-white font-semibold">comprensión</span>.
          </p>
          <p className="text-gray-300 mb-6">
            Por eso, si tenés críticas y/o sugerencias, envianos un mail de <span className="text-lactal-gold font-bold">3:00 a 3:15 AM</span> a:
          </p>
          <div className="bg-black/40 border border-lactal-gold/30 rounded-lg p-4 mb-2">
            <p className="text-lactal-gold text-lg sm:text-xl font-bold whitespace-nowrap">
              sosunviejoverga@gmail.com
            </p>
          </div>
        </div>
        <Button onClick={onClose} className="btn-primary w-full">
          <span>Entendido</span>
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const OpinionSection = () => {
  const [showOpinionModal, setShowOpinionModal] = useState(false);
  
  return (
    <section className="py-12 px-4" data-testid="opinion-section">
      <div className="max-w-4xl mx-auto text-center">
        <button
          onClick={() => setShowOpinionModal(true)}
          className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-500 hover:to-blue-700 text-white font-bold px-8 py-4 rounded-lg text-lg transition-all transform hover:scale-105 flex items-center gap-3 mx-auto shadow-lg shadow-blue-900/30"
          data-testid="opinion-btn"
        >
          <img src={TU_OPINION_ICON} alt="" className="w-8 h-8 object-contain" />
          <span className="font-heading uppercase tracking-wider">Tu opinión nos importa</span>
        </button>
      </div>
      <OpinionModal isOpen={showOpinionModal} onClose={() => setShowOpinionModal(false)} />
    </section>
  );
};

// Quiénes Somos Section
const RESPONSABLES = [
  {
    nombre: "Raúl Coimazo",
    cargo: "Organización General",
    imagen: "https://customer-assets.emergentagent.com/job_club-tournament/artifacts/9liie3hn_Organizador_General.png"
  },
  {
    nombre: "Stevie Arreglini",
    cargo: "Responsable de Jueces",
    imagen: "https://customer-assets.emergentagent.com/job_club-tournament/artifacts/pstw7j1a_Responsable_Arbitraje.png"
  },
  {
    nombre: "Mario Retiro",
    cargo: "Tesorero General",
    imagen: "https://customer-assets.emergentagent.com/job_veterans-soccer/artifacts/ledypapc_Tesorero.png"
  },
  {
    nombre: "La Moria Cash",
    cargo: "Producción General",
    imagen: "https://customer-assets.emergentagent.com/job_club-tournament/artifacts/kv6p8xio_Produccion_General.png"
  }
];

// Image Lightbox Modal
const ImageLightbox = ({ isOpen, onClose, imageUrl, altText }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
        aria-label="Cerrar"
      >
        <X size={32} />
      </button>
      <img 
        src={imageUrl} 
        alt={altText}
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

const QuienesSomosSection = ({ onOpenOpinionModal }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  
  return (
    <section id="quienes-somos" className="py-16 px-4" data-testid="quienes-somos-section">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title text-center mb-2">
          <Users className="inline-block mr-2 text-lactal-gold" size={32} />
          Quiénes Somos
        </h2>
        
        <h3 className="font-heading text-xl text-lactal-gold uppercase tracking-wider text-center mb-4">
          Responsables
        </h3>
        
        <p className="text-gray-300 text-center max-w-2xl mx-auto mb-10">
          Nuestro equipo de profesionales garantiza la más absoluta <span className="text-white font-semibold">transparencia</span>, <span className="text-white font-semibold">equidad</span> y <span className="text-white font-semibold">compromiso</span> en cada torneo. 
          Con años de experiencia y trayectoria trabajamos incansablemente para que todos ganemos, ganarán los que <span className="text-lactal-gold font-semibold italic">más pongan!</span> ;)
          <br /><br />
          Si tenés alguna duda o sugerencia, no dudes en{" "}
          <button 
            onClick={onOpenOpinionModal}
            className="text-lactal-gold hover:text-yellow-300 underline font-semibold transition-colors"
          >
            contactarnos
          </button>
        </p>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {RESPONSABLES.map((responsable, idx) => (
            <div 
              key={idx}
              className="text-center animate-fade-in"
              style={{ animationDelay: `${idx * 0.1}s` }}
            >
              <button
                onClick={() => setSelectedImage(responsable)}
                className="relative w-24 h-32 sm:w-28 sm:h-36 mx-auto mb-3 rounded-lg overflow-hidden border-2 border-lactal-gold/30 bg-black/30 cursor-pointer hover:border-lactal-gold hover:scale-105 transition-all group"
                data-testid={`responsable-img-${idx}`}
              >
                <img 
                  src={responsable.imagen} 
                  alt={responsable.nombre}
                  className="w-full h-full object-cover object-top group-hover:brightness-110 transition-all"
                />
                {/* Indicador de zoom - visible siempre en móvil, en hover en desktop */}
                <div className="absolute inset-0 bg-black/30 md:bg-black/0 md:group-hover:bg-black/40 transition-all flex items-center justify-center">
                  <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity bg-lactal-gold/90 rounded-full p-1.5 sm:p-2 shadow-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-black sm:w-5 sm:h-5">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.3-4.3"></path>
                      <path d="M11 8v6"></path>
                      <path d="M8 11h6"></path>
                    </svg>
                  </div>
                </div>
              </button>
              <h4 className="font-heading text-white font-bold text-sm sm:text-lg uppercase">
                {responsable.nombre}
              </h4>
              <p className="text-gray-400 text-xs sm:text-sm">
                {responsable.cargo}
              </p>
            </div>
          ))}
        </div>
      </div>
      
      <ImageLightbox 
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageUrl={selectedImage?.imagen}
        altText={selectedImage?.nombre}
      />
    </section>
  );
};

// Galería Section
const GaleriaSection = ({ galeria, loading }) => {
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Helper to get full URL for local uploads
  const getMediaUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/uploads/')) {
      // Legacy path - convert to new path
      return `${BACKEND_URL}/api${url}`;
    }
    if (url.startsWith('/api/uploads/')) {
      return `${BACKEND_URL}${url}`;
    }
    return url;
  };
  
  // Helper to get video embed URL
  const getVideoEmbedUrl = (url) => {
    if (!url) return '';
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('/').pop()
        : url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    // Local video - return as is
    return getMediaUrl(url);
  };
  
  // Check if video is local (uploaded file)
  const isLocalVideo = (url) => {
    return url?.startsWith('/uploads/') || url?.startsWith('/api/uploads/');
  };
  
  return (
    <section id="galeria" className="py-16 px-4" data-testid="galeria-section">
      <div className="max-w-4xl mx-auto">
        <h2 className="section-title text-center mb-2">
          <Image className="inline-block mr-2 text-lactal-gold" size={32} />
          Galería
        </h2>
        <p className="section-subtitle text-center mb-8">
          Fotos y videos del torneo
        </p>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-400 font-heading text-lg">Cargando galería...</p>
          </div>
        ) : galeria.length === 0 ? (
          <div className="text-center py-12 bg-black/30 backdrop-blur-sm rounded-lg border border-white/10">
            <Image size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 font-heading text-lg mb-2">
              Próximamente
            </p>
            <p className="text-gray-500 text-sm">
              Acá iremos subiendo fotos y videos del torneo. ¡Mantenete atento!
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galeria.map((item, idx) => (
              <button 
                key={item.id || idx}
                onClick={() => setSelectedItem(item)}
                className="relative aspect-video bg-black/30 rounded-lg overflow-hidden border border-white/10 group cursor-pointer hover:border-lactal-gold/50 transition-all animate-fade-in"
                style={{ animationDelay: `${idx * 0.05}s` }}
                data-testid={`galeria-item-${idx}`}
              >
                {item.tipo === 'video' ? (
                  <>
                    <img 
                      src={item.thumbnail ? getMediaUrl(item.thumbnail) : (isLocalVideo(item.url) ? '' : `https://img.youtube.com/vi/${item.url.split('v=')[1]?.split('&')[0]}/0.jpg`)} 
                      alt={item.titulo || 'Video del torneo'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Play size={48} className="text-white opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
                    </div>
                  </>
                ) : (
                  <img 
                    src={getMediaUrl(item.url)} 
                    alt={item.titulo || 'Foto del torneo'}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                )}
                {item.titulo && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <p className="text-white text-sm truncate">{item.titulo}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Lightbox for images and videos */}
      {selectedItem && (
        <div 
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setSelectedItem(null)}
          data-testid="galeria-lightbox"
        >
          <button
            onClick={() => setSelectedItem(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
            aria-label="Cerrar"
          >
            <X size={32} />
          </button>
          
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            {selectedItem.tipo === 'video' ? (
              <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                {isLocalVideo(selectedItem.url) ? (
                  <video
                    src={getMediaUrl(selectedItem.url)}
                    controls
                    autoPlay
                    className="w-full h-full"
                  >
                    Tu navegador no soporta el elemento de video.
                  </video>
                ) : (
                  <iframe
                    src={getVideoEmbedUrl(selectedItem.url)}
                    title={selectedItem.titulo || 'Video'}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )}
              </div>
            ) : (
              <img 
                src={getMediaUrl(selectedItem.url)} 
                alt={selectedItem.titulo || 'Imagen'}
                className="max-w-full max-h-[85vh] mx-auto object-contain rounded-lg"
              />
            )}
            {(selectedItem.titulo || selectedItem.descripcion) && (
              <div className="mt-4 text-center">
                {selectedItem.titulo && (
                  <h3 className="text-white font-heading text-xl">{selectedItem.titulo}</h3>
                )}
                {selectedItem.descripcion && (
                  <p className="text-gray-400 mt-2">{selectedItem.descripcion}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

// Footer
const Footer = ({ onAdminClick }) => {
  return (
    <footer className="py-8 px-4 border-t border-white/5 bg-black" data-testid="footer">
      <div className="max-w-4xl mx-auto text-center">
        <img src={LOGO_BLANCO} alt="LACTAL" className="h-10 mx-auto mb-4 opacity-70" />
        <p className="text-gray-500 text-sm mb-4">
          Torneo LACTAL 2026 • El Torneo Mata Buzarda
        </p>
        <button
          onClick={onAdminClick}
          className="text-gray-600 hover:text-gray-400 text-xs flex items-center gap-1 mx-auto transition-colors"
          data-testid="admin-login-btn"
        >
          <Lock size={12} />
          Admin
        </button>
      </div>
    </footer>
  );
};

// Main Home component
const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOpinionModalOpen, setIsOpinionModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");
  const [players, setPlayers] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [partidos, setPartidos] = useState([]);
  const [galeria, setGaleria] = useState([]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [playersRes, equiposRes, partidosRes, infoRes, galeriaRes] = await Promise.all([
        axios.get(`${API}/participantes/publico`),
        axios.get(`${API}/equipos/con-jugadores`),
        axios.get(`${API}/partidos/con-equipos`),
        axios.get(`${API}/info-torneo`),
        axios.get(`${API}/galeria`)
      ]);
      setPlayers(playersRes.data);
      setEquipos(equiposRes.data);
      setPartidos(partidosRes.data);
      setInfo(infoRes.data);
      setGaleria(galeriaRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["hero", "info", "inscriptos", "equipos", "fixture", "galeria", "quienes-somos"];
      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavigate = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -80;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const handleRegistrationSuccess = () => {
    toast.success("¡Adentro! Ya estás en la lista.", {
      description: "Te inscribiste correctamente al torneo."
    });
    fetchData();
    setIsModalOpen(false);
  };

  const handleAdminClick = () => {
    navigate("/admin");
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Header 
        onOpenModal={() => setIsModalOpen(true)} 
        onNavigate={handleNavigate}
        activeSection={activeSection}
      />
      
      <main>
        <HeroSection 
          onOpenModal={() => setIsModalOpen(true)} 
          totalPlayers={players.length}
        />
        <InfoSection info={info} />
        <InscriptosSection players={players} loading={loading} />
        <EquiposSection equipos={equipos} loading={loading} />
        <FixtureSection partidos={partidos} equipos={equipos} loading={loading} />
        <GaleriaSection galeria={galeria} loading={loading} />
        <QuienesSomosSection onOpenOpinionModal={() => setIsOpinionModalOpen(true)} />
        <OpinionSection />
      </main>

      <Footer onAdminClick={handleAdminClick} />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleRegistrationSuccess}
      />
      
      <OpinionModal 
        isOpen={isOpinionModalOpen} 
        onClose={() => setIsOpinionModalOpen(false)} 
      />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#252525',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      />
      <BrowserRouter basename="web">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<AdminPanel />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
